import { serverNow } from '#features/date/utils/serverClock';

/**
 * Статусы сессии, при которых она уже ничего не ждёт от игроков.
 *
 * Завершение («Finish session») и отмена не двигают `expiredAt`, поэтому у закрытой сессии
 * время окончания часто ещё в будущем. Любая витрина, которая судит о «живости» по одному лишь
 * интервалу времени, рисует такой сессии LIVE и обратный отсчёт — так вело себя окно архива.
 */
export const ARCHIVED_SESSION_STATUSES = new Set(['COMPLETED', 'CANCELLED']);

/** Сессия закрыта — независимо от того, истекло её время или ещё нет. */
export const isArchivedSessionStatus = (status?: string | null) =>
  ARCHIVED_SESSION_STATUSES.has(status ?? '');

/** The server already knows that a Play Now session is live, even if this device is behind it. */
export const isLiveSession = (
  session: {
    status?: string | null;
    startedAt?: string | null;
    expiredAt?: string | null;
  },
  now = serverNow()
) => {
  if (isArchivedSessionStatus(session.status)) return false;

  const start = Date.parse(session.startedAt ?? '');
  const end = Date.parse(session.expiredAt ?? '');
  return (
    Number.isFinite(start) &&
    Number.isFinite(end) &&
    start < end &&
    now < end &&
    (session.status === 'LIVE' || now >= start)
  );
};

/** A server-confirmed live session must not also appear among upcoming sessions. */
export const isUpcomingSession = (
  session: {
    status?: string | null;
    startedAt?: string | null;
    expiredAt?: string | null;
  },
  now = serverNow()
) => {
  if (isArchivedSessionStatus(session.status) || session.status === 'LIVE') return false;

  const start = Date.parse(session.startedAt ?? '');
  const end = Date.parse(session.expiredAt ?? '');
  return Number.isFinite(start) && Number.isFinite(end) && now < start && start < end;
};
